import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CardFor } from '../cards';
import MarkdownText from '../MarkdownText';
import { setAssistantStatus, setWaiting, markEventsStale } from '../store/chatSlice';
import { sendAction, sendCardInvoke } from '../store/Actions';
import { cardIndexEntry } from '../cards/sandbox/cardIndex';
import { cardFreshnessKey } from '../cards/freshnessKey';
import { isReinvokeEmbed } from '../store/websocketMiddleware';

// CardViewer (gallery-fit-content) — the content-card analog of GameViewer. Renders an
// emitted card as a single sandboxed card, keyed on session/__embedGen so a re-emit or an embed
// refresh re-mounts it (freshness — the reason a content card would otherwise reach for viewer=
// "game"), but WITHOUT `fillHeight`. The card reports its own content height (card:resize), so an
// embed / Gallery tile can size to the content instead of stretching it to fill a fixed box.
//
// gallery-card-pager: two modes.
//   default        — the original single-LATEST-card behavior (the ViewerRegistry "card" viewer;
//                    chat semantics unchanged: the newest emit is the live card).
//   pager (embeds) — a saved action can emit SEVERAL cards (the re-invoke returns them all);
//                    render ONE at a time, first card first, with Previous/Next controls. Replaces
//                    the old fallback where a multi-card embed dropped into TextViewer's bordered
//                    chat-transcript box. Paging is client-side over the already-fetched cards —
//                    it never re-invokes the helper.

// Mirrors TextViewer.parseEvent: a structured event carries text / kind / action(s).
function parseEvent(msg) {
  const isStructured = (v) => v && typeof v === 'object' &&
    (v.text || v.kind || v.action || v.actions);
  if (isStructured(msg)) return msg;
  try {
    const parsed = JSON.parse(msg);
    if (isStructured(parsed)) return parsed;
  } catch (e) { /* plain text */ }
  return null;
}

// Freshness key — see `cards/freshnessKey.js`. One implementation, both viewers:
// the two used to disagree about when a card re-mounts, and the chat path lost.
const cardKey = cardFreshnessKey;

// card-pager-swipe: nearest horizontally-scrollable ancestor of `el`, so a swipe that a
// scroll region should consume isn't stolen for paging. Only reachable for cards rendered
// INLINE in the parent DOM (build-time plugins, text, GenericCard) — a sandboxed card
// swallows its own touches and runs the same check inside the iframe (buildCardDocument).
// KEEP THIS + the onTouchEnd yield/threshold logic in sync with the in-iframe copy in
// buildCardDocument.js (CARD_SDK_BOOTSTRAP swipe block) — they must decide identically.
function hScrollerFrom(el) {
  for (; el && el.nodeType === 1 && el !== document.body; el = el.parentNode) {
    if (el.scrollWidth - el.clientWidth > 2) {
      const ox = window.getComputedStyle ? getComputedStyle(el).overflowX : '';
      if (ox === 'auto' || ox === 'scroll') return el;
    }
  }
  return null;
}

const CardViewer = ({ events, pager, onPageChange }) => {
  const [page, setPage] = useState(0);
  // card-pager-swipe: swipe-gesture scratch, in a ref so it survives a re-render between
  // touchstart and touchend.
  const swipeTouch = React.useRef({ x: 0, y: 0, sc: null, on: false });
  const dispatch = useDispatch();
  const { station, threadId, user } = useSelector(state => state.chat);

  // Pager mode: every structured event is a card; page through them in emit order.
  const cards = pager
    ? (events || []).map(parseEvent).filter(Boolean)
    : [];

  // Clamp — a refresh can shrink the set (fewer cards this run); an equal-size refresh keeps the
  // viewer on the same page so "Refresh" means "this card, fresh", not "back to card 1".
  useEffect(() => {
    if (pager) setPage((p) => Math.max(0, Math.min(p, cards.length - 1)));
  }, [pager, cards.length]);

  // Default (non-pager) mode: the latest event only — unchanged chat semantics.
  const [eventData, setEventData] = useState(null);
  useEffect(() => {
    if (!pager && events && events.length > 0) {
      const latest = events[events.length - 1];
      if (latest) {
        try {
          const parsed = typeof latest === 'object' ? latest : JSON.parse(latest);
          setEventData(parsed || null);
        } catch (e) {
          setEventData(null);
        }
      }
    }
  }, [pager, events]);

  const handleAction = (action, structured) => {
    // No station/WS at all (a read-only, non-actable tile) — actions stay inert,
    // same bail as TextViewer's (without it the dispatch below throws).
    if (!station || !station.station) return;
    // tile-card-actions: an ACTABLE re-invoke page has a station + socket but NO
    // thread — assist:action would be dropped silently by the agent. Translate
    // the card's action_spec onto the tile channel (qlink:invoke) and nudge the
    // page's post-invoke refresh; the re-render IS the feedback (e.g. Tobin's
    // claimed-by-you state).
    if (isReinvokeEmbed()) {
      // canvas-card (tile pass): a `renderer:` card (Canvas) is rendered by THIS page and
      // reads its reply as new props — so the invoke asks the rail for the board back whole
      // (`inline`), and the page does NOT nudge its refresh: a refresh remounts the card and
      // loses the playhead, the armed tool and the ▶ loop on every click, which is exactly
      // the remount bug embed-tile-session cured for sandboxed cards. Everything else (a
      // GenericCard's Apply, a Tobin claim) keeps the refresh-is-the-feedback path.
      const kind = structured && structured.kind;
      cardIndexEntry(kind).then((entry) => {
        const inline = !!(entry && entry.renderer);
        dispatch(sendCardInvoke(action, station.station.station_id, station.sessionId, inline));
        if (inline) return;
        try {
          window.dispatchEvent(new CustomEvent('aveq:card-invoked', { detail: { kind } }));
        } catch (e) { /* no CustomEvent — no refresh nudge */ }
      });
      return;
    }
    dispatch(setWaiting(true));
    dispatch(markEventsStale());
    dispatch(sendAction(action, station.station.station_id, station.sessionId, threadId));
    const confirmation = action.confirmation
      || (structured && structured.summary
        ? `${action.label || 'Action'}: ${structured.summary}`
        : (action.label || 'Action'));
    // ephemeral-status-beats: transient status line, not a persisted bubble.
    dispatch(setAssistantStatus(confirmation));
  };

  const waiting = (
    <div style={{ padding: 16, color: '#888', textAlign: 'center' }}>Waiting for card…</div>
  );

  // One card, chrome-free, auto-sizing. A card with kind/action(s) routes through the registry
  // (GenericCard fallback); a text-only structured value renders as prose, mirroring TextViewer —
  // requiring `kind` here would blank e.g. JobSearch's text+action cards.
  const renderCard = (structured, idx, onSwipe) => (
    (structured.kind || structured.action || structured.actions) ? (
      <CardFor key={cardKey(structured, idx)} structured={structured} onAction={handleAction}
        accountId={user && user.user_id} onSwipe={onSwipe} />
    ) : (
      <div key={cardKey(structured, idx)} style={{ margin: '12px 4px', fontSize: '0.9rem', lineHeight: 1.5 }}>
        <MarkdownText text={structured.text} />
      </div>
    )
  );

  if (!pager) {
    if (!eventData || !eventData.kind) return waiting;
    // No fixed height, no fill — the card sizes to its own content.
    return (
      <div style={{ boxSizing: 'border-box' }}>
        {renderCard(eventData, 0)}
      </div>
    );
  }

  if (cards.length === 0) return waiting;
  const current = Math.max(0, Math.min(page, cards.length - 1));

  // card-pager-swipe: page on a horizontal swipe (touch) or ← / → keys, in addition to
  // the arrow buttons. Bounds-clamped. A swipe arrives two ways, mutually exclusive per
  // card: from a sandboxed card via postMessage (SandboxedCard → CardFor onSwipe), or —
  // for a card rendered inline in this DOM — from the touch handlers on the container
  // below (an iframe swallows its own touches, so only one path fires for a given card).
  // gallery-card-paging: EVERY page transition goes through here — swipe, arrows, and the
  // sandboxed-card postMessage path — so `onPageChange` cannot be reported by one route and
  // missed by another. It fires only when the page ACTUALLY moved, which is also what keeps
  // the paired screen agreeing with this pager at the ends: this side clamps (Next is
  // disabled on the last card) while the device WRAPS, and a disagreement about the ends was
  // a real bug once between the Control card and a tap on the glass. Nothing moves here →
  // nothing is sent → nothing moves there.
  const goToPage = (target) => {
    const next = Math.max(0, Math.min(target, cards.length - 1));
    if (next === current) return;
    setPage(next);
    if (onPageChange) onPageChange(next - current, next);
  };
  const swipe = (dir) => goToPage(dir === 'next' ? current + 1 : current - 1);
  const onTouchStart = (e) => {
    const s = swipeTouch.current;
    if (!e.touches || e.touches.length !== 1) { s.on = false; return; }
    s.x = e.touches[0].clientX; s.y = e.touches[0].clientY;
    s.sc = hScrollerFrom(e.target); s.on = true;
  };
  const onTouchEnd = (e) => {
    const s = swipeTouch.current;
    if (!s.on) return;
    s.on = false;
    const t = e.changedTouches && e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - s.x, dy = t.clientY - s.y;
    if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.4) return;   // not a horizontal swipe
    if (s.sc) {   // an inner scroller that can still move this way owns the gesture
      const maxL = s.sc.scrollWidth - s.sc.clientWidth;
      if (dx > 0 && s.sc.scrollLeft > 1) return;
      if (dx < 0 && s.sc.scrollLeft < maxL - 1) return;
    }
    swipe(dx < 0 ? 'next' : 'prev');
  };
  const onKeyDown = (e) => {
    if (e.key === 'ArrowLeft') swipe('prev');
    else if (e.key === 'ArrowRight') swipe('next');
    else return;
    e.preventDefault();
  };

  const chevron = (points) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points={points} />
    </svg>
  );
  // ~40px touch targets (gallery-touch-targets); the pager row is FIXED chrome below the card —
  // present whenever there are 2+ cards — so flipping pages never shifts the row itself (the
  // card region above it may still resize to the new card's content; the tile follows via the
  // height beacon).
  const navBtn = (disabled) => ({
    background: 'none', border: 'none', borderRadius: 8, padding: 9,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minWidth: 40, minHeight: 40, color: disabled ? '#cbd5e1' : '#64748b',
    cursor: disabled ? 'default' : 'pointer',
  });
  // card-pager-swipe: swipe / arrow-key paging is only meaningful with 2+ cards. The touch
  // handlers on the container catch swipes on cards rendered INLINE in this DOM; a sandboxed
  // card forwards its own via onSwipe. Detection is passive (no preventDefault), so vertical
  // scrolling of the drawer is unaffected.
  const multi = cards.length > 1;
  const pagerProps = multi
    ? { onTouchStart, onTouchEnd, onKeyDown, tabIndex: 0, role: 'group', 'aria-roledescription': 'carousel' }
    : {};
  return (
    <div className={multi ? 'card-pager' : undefined} style={{ boxSizing: 'border-box' }} {...pagerProps}>
      {renderCard(cards[current], current, multi ? swipe : undefined)}
      {multi && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          padding: '2px 6px',
        }}>
          <button type="button" aria-label="Previous card" title="Previous card"
            disabled={current === 0} onClick={() => goToPage(current - 1)}
            style={navBtn(current === 0)}>
            {chevron('15 18 9 12 15 6')}
          </button>
          <span aria-live="polite" style={{ fontSize: '0.78rem', color: '#64748b', minWidth: 44, textAlign: 'center' }}>
            {current + 1} / {cards.length}
          </span>
          <button type="button" aria-label="Next card" title="Next card"
            disabled={current === cards.length - 1} onClick={() => goToPage(current + 1)}
            style={navBtn(current === cards.length - 1)}>
            {chevron('9 18 15 12 9 6')}
          </button>
        </div>
      )}
    </div>
  );
};

export default CardViewer;
